import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight, Download, Receipt, Search, SlidersHorizontal, X } from 'lucide-react'
import { useDatos } from '../store/datos'
import { useSesion } from '../store/sesion'
import { useUI } from '../store/ui'
import { Encabezado, Pagina } from '../components/Layout'
import { SubNavVentas } from '../components/SubNavVentas'
import { Avatar, Boton, Campo, Chip, Entrada, Lista, Tarjeta, Vacio } from '../components/ui/base'
import { Hoja } from '../components/ui/Hoja'
import { exportarVentasExcel } from '../export/excel'
import { cn } from '../lib/cn'
import { TONO_ESTADO } from '../lib/tonos'
import { fecha, hora, money, normalizar, num } from '../lib/format'
import { ESTADOS_PEDIDO, METODOS_PAGO, type MetodoPago } from '../types'

interface Filtros {
  vendedor: string
  metodoPago: string
  estado: string
  tipoCliente: string
  desde: string
  hasta: string
}

const FILTROS_VACIOS: Filtros = {
  vendedor: '',
  metodoPago: '',
  estado: '',
  tipoCliente: '',
  desde: '',
  hasta: '',
}

export function Ventas() {
  const { ventas, usuarios } = useDatos()
  const usuario = useSesion((s) => s.usuario)!
  const avisar = useUI((s) => s.avisar)
  const esAdmin = usuario.rol === 'admin'

  const [texto, setTexto] = useState('')
  const [filtros, setFiltros] = useState<Filtros>(FILTROS_VACIOS)
  const [panel, setPanel] = useState(false)

  // Un vendedor solo ve lo suyo. No es una preferencia: es el permiso.
  const base = useMemo(
    () => (esAdmin ? ventas : ventas.filter((v) => v.usuarioId === usuario.id)),
    [ventas, esAdmin, usuario.id],
  )

  const lista = useMemo(() => {
    const q = normalizar(texto.trim())
    return base.filter((v) => {
      if (
        q &&
        ![v.folio, v.clienteNombre, v.usuarioNombre, ...v.lineas.map((l) => l.nombre)].some((c) =>
          normalizar(c).includes(q),
        )
      )
        return false
      if (filtros.vendedor && v.usuarioId !== filtros.vendedor) return false
      if (filtros.metodoPago && v.metodoPago !== filtros.metodoPago) return false
      if (filtros.estado && v.estado !== filtros.estado) return false
      if (filtros.tipoCliente && v.clienteTipo !== filtros.tipoCliente) return false
      if (filtros.desde && v.fecha < filtros.desde) return false
      if (filtros.hasta && v.fecha > `${filtros.hasta}T23:59:59`) return false
      return true
    })
  }, [base, texto, filtros])

  const total = lista.reduce((s, v) => s + (v.estado === 'anulada' ? 0 : v.total), 0)
  const activos = Object.values(filtros).filter(Boolean).length

  // Las ventas se agrupan por día: así se lee el ritmo del negocio.
  const porDia = useMemo(() => {
    const mapa = new Map<string, typeof lista>()
    for (const v of lista) {
      const clave = v.fecha.slice(0, 10)
      mapa.set(clave, [...(mapa.get(clave) ?? []), v])
    }
    return [...mapa.entries()].sort((a, b) => b[0].localeCompare(a[0]))
  }, [lista])

  return (
    <Pagina>
      <SubNavVentas activa="ventas" />
      <Encabezado
        titulo={esAdmin ? 'Ventas' : 'Mis ventas'}
        detalle={`${num(lista.length)} ${lista.length === 1 ? 'venta' : 'ventas'} · ${money(total)}`}
        accion={
          <Boton
            variante="secundario"
            icono={<Download className="size-[18px]" />}
            onClick={async () => {
              await exportarVentasExcel(lista, 'ventas-novaberry')
              avisar('Excel descargado')
            }}
            disabled={!lista.length}
          >
            Excel
          </Boton>
        }
      />

      <div className="mb-4 flex gap-2">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute top-1/2 left-4 size-[18px] -translate-y-1/2 texto-tenue" />
          <input
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="Folio, cliente, producto o vendedor…"
            className="h-12 w-full rounded-2xl border borde-base superficie-elevada pr-11 pl-11 text-[15px] texto-base transition-colors placeholder:texto-tenue focus:border-bosque-400 focus:outline-none focus:ring-4 focus:ring-bosque-500/10"
          />
          {texto && (
            <button
              onClick={() => setTexto('')}
              className="absolute top-1/2 right-3 grid size-8 -translate-y-1/2 place-items-center rounded-lg texto-tenue hover:texto-base"
              aria-label="Borrar búsqueda"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
        <button
          onClick={() => setPanel(true)}
          className="relative grid size-12 shrink-0 place-items-center rounded-2xl border borde-base superficie-elevada texto-suave transition-colors hover:superficie-hundida hover:texto-base"
          aria-label="Filtrar"
        >
          <SlidersHorizontal className="size-5" />
          {activos > 0 && (
            <span className="absolute -top-1 -right-1 grid size-5 place-items-center rounded-full bg-bosque-500 text-[10px] font-bold text-white">
              {activos}
            </span>
          )}
        </button>
      </div>

      {activos > 0 && (
        <button
          onClick={() => setFiltros(FILTROS_VACIOS)}
          className="mb-4 inline-flex items-center gap-1.5 rounded-full superficie-hundida px-3 py-1.5 text-[12px] font-semibold texto-suave hover:texto-base"
        >
          <X className="size-3.5" /> Quitar {activos} {activos === 1 ? 'filtro' : 'filtros'}
        </button>
      )}

      {lista.length === 0 ? (
        <Vacio
          icono={<Receipt className="size-7" />}
          titulo="No hay ventas que mostrar"
          detalle={
            texto || activos
              ? 'Ajusta la búsqueda o quita los filtros.'
              : 'Registra tu primera venta desde el botón verde.'
          }
          accion={
            !texto && !activos && !esAdmin ? (
              <Link to="/vender">
                <Boton>Nueva venta</Boton>
              </Link>
            ) : undefined
          }
        />
      ) : (
        <div className="space-y-5">
          {porDia.map(([dia, delDia]) => (
            <div key={dia}>
              <div className="mb-2 flex items-baseline justify-between px-1">
                <h3 className="text-[13px] font-bold texto-suave">{fecha(dia)}</h3>
                <span className="cifra text-[13px] font-semibold texto-tenue">
                  {money(delDia.reduce((s, v) => s + (v.estado === 'anulada' ? 0 : v.total), 0))}
                </span>
              </div>
              <Tarjeta className="overflow-hidden">
                <ul className="divide-y borde-suave">
                  {delDia.map((v) => (
                    <li key={v.id}>
                      <Link
                        to={`/ventas/${v.id}`}
                        className="flex items-center gap-3 px-4 py-3.5 transition-colors hover:superficie-hundida sm:px-5"
                      >
                        <Avatar nombre={v.clienteNombre} tamano="sm" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[15px] font-semibold texto-base">
                            {v.clienteNombre}
                          </p>
                          <p className="truncate text-[12px] texto-suave">
                            <span className="codigo">{v.folio}</span> · {hora(v.fecha)} ·{' '}
                            {v.usuarioNombre}
                          </p>
                          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                            <Chip className={TONO_ESTADO[v.estado]}>
                              {ESTADOS_PEDIDO.find((e) => e.valor === v.estado)?.etiqueta}
                            </Chip>
                            <Chip>
                              {METODOS_PAGO.find((m) => m.valor === v.metodoPago)?.etiqueta}
                            </Chip>
                            {!v.pagado && v.estado !== 'anulada' && (
                              <Chip className="bg-ambar-50 text-ambar-600 dark:bg-ambar-500/15 dark:text-ambar-300">
                                Por cobrar{v.vencimientoPago ? ` · vence ${fecha(v.vencimientoPago)}` : ''}
                              </Chip>
                            )}
                          </div>
                        </div>
                        <div className="shrink-0 text-right">
                          <p
                            className={cn(
                              'cifra text-[16px] font-bold',
                              v.estado === 'anulada' ? 'texto-tenue line-through' : 'texto-base',
                            )}
                          >
                            {money(v.total)}
                          </p>
                          <p className="text-[11px] texto-tenue">
                            {v.lineas.length} {v.lineas.length === 1 ? 'ítem' : 'ítems'}
                          </p>
                        </div>
                        <ChevronRight className="size-4 shrink-0 texto-tenue" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </Tarjeta>
            </div>
          ))}
        </div>
      )}

      <Hoja
        abierta={panel}
        onCerrar={() => setPanel(false)}
        titulo="Filtrar ventas"
        ancho="sm"
        pie={
          <>
            <Boton variante="secundario" bloque onClick={() => setFiltros(FILTROS_VACIOS)}>
              Limpiar
            </Boton>
            <Boton bloque onClick={() => setPanel(false)}>
              Ver {lista.length} {lista.length === 1 ? 'venta' : 'ventas'}
            </Boton>
          </>
        }
      >
        <div className="space-y-4 pb-2">
          {esAdmin && (
            <Campo etiqueta="Vendedor">
              <Lista
                value={filtros.vendedor}
                onChange={(e) => setFiltros({ ...filtros, vendedor: e.target.value })}
              >
                <option value="">Todos</option>
                {usuarios.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.nombre}
                  </option>
                ))}
              </Lista>
            </Campo>
          )}

          <Campo etiqueta="Método de pago">
            <Lista
              value={filtros.metodoPago}
              onChange={(e) => setFiltros({ ...filtros, metodoPago: e.target.value as MetodoPago })}
            >
              <option value="">Todos</option>
              {METODOS_PAGO.map((m) => (
                <option key={m.valor} value={m.valor}>
                  {m.etiqueta}
                </option>
              ))}
            </Lista>
          </Campo>

          <Campo etiqueta="Estado">
            <Lista
              value={filtros.estado}
              onChange={(e) => setFiltros({ ...filtros, estado: e.target.value })}
            >
              <option value="">Todos</option>
              {ESTADOS_PEDIDO.map((e) => (
                <option key={e.valor} value={e.valor}>
                  {e.etiqueta}
                </option>
              ))}
            </Lista>
          </Campo>

          <Campo etiqueta="Tipo de cliente">
            <Lista
              value={filtros.tipoCliente}
              onChange={(e) => setFiltros({ ...filtros, tipoCliente: e.target.value })}
            >
              <option value="">Todos</option>
              <option value="empresa">Empresas</option>
              <option value="particular">Particulares</option>
            </Lista>
          </Campo>

          <div className="grid grid-cols-2 gap-3">
            <Campo etiqueta="Desde">
              <Entrada
                type="date"
                value={filtros.desde}
                onChange={(e) => setFiltros({ ...filtros, desde: e.target.value })}
              />
            </Campo>
            <Campo etiqueta="Hasta">
              <Entrada
                type="date"
                value={filtros.hasta}
                onChange={(e) => setFiltros({ ...filtros, hasta: e.target.value })}
              />
            </Campo>
          </div>
        </div>
      </Hoja>
    </Pagina>
  )
}
